import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getPlayerId, getSavedName, saveName } from '../../utils/playerIdentity';
import { fetchProfile, updateProfileName, FullProfile } from '../../utils/api';
import { socket } from '../../socket';
import './Profile.css';

interface ProfileProps {
  open: boolean;
  onClose: () => void;
}

const Profile: React.FC<ProfileProps> = ({ open, onClose }) => {
  const [profile, setProfile] = useState<FullProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const playerId = getPlayerId();
  const titleId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError('');
    const data = await fetchProfile(playerId);
    if (data) {
      setProfile(data);
    } else {
      const savedName = getSavedName();
      setProfile({
        name: savedName || 'Guest',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        gamesPlayed: 0,
        gamesWon: 0,
        gamesLost: 0,
        totalCuts: 0,
      });
    }
    setLoading(false);
  }, [playerId]);

  useEffect(() => {
    if (open) loadProfile();
  }, [open, loadProfile]);

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    setSaving(true);
    const success = await updateProfileName(playerId, trimmed);
    if (success) {
      saveName(trimmed);
      socket.emit('update_name', { playerId, name: trimmed });
      setProfile((prev) => prev ? { ...prev, name: trimmed } : prev);
      setEditingName(false);
    } else {
      saveName(trimmed);
      setProfile((prev) => prev ? { ...prev, name: trimmed } : prev);
      setEditingName(false);
    }
    setSaving(false);
  };

  const winRate =
    profile && profile.gamesPlayed > 0
      ? ((profile.gamesWon / profile.gamesPlayed) * 100).toFixed(1)
      : '0.0';

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="profile-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="profile-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="profile-close" onClick={onClose} aria-label="Close profile">
            &times;
          </button>

          <h2 id={titleId} className="profile-title">Player Profile</h2>

          {loading ? (
            <div className="profile-loading">Loading...</div>
          ) : profile ? (
            <>
              <div className="profile-name-section">
                {editingName ? (
                  <div className="profile-name-edit">
                    <input
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      maxLength={16}
                      autoFocus
                      placeholder="Enter name"
                    />
                    <button
                      className="profile-name-save"
                      onClick={handleSaveName}
                      disabled={saving || !nameInput.trim()}
                    >
                      {saving ? '...' : 'Save'}
                    </button>
                    <button
                      className="profile-name-cancel"
                      onClick={() => setEditingName(false)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="profile-name-display">
                    <span className="profile-display-name">{profile.name}</span>
                    <button
                      className="profile-edit-btn"
                      onClick={() => {
                        setNameInput(profile.name);
                        setEditingName(true);
                      }}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>

              {error && <p className="profile-error">{error}</p>}

              <div className="profile-stats-grid">
                <div className="stat-card">
                  <span className="stat-value">{profile.gamesPlayed}</span>
                  <span className="stat-label">Games Played</span>
                </div>
                <div className="stat-card wins">
                  <span className="stat-value">{profile.gamesWon}</span>
                  <span className="stat-label">Wins</span>
                </div>
                <div className="stat-card losses">
                  <span className="stat-value">{profile.gamesLost}</span>
                  <span className="stat-label">Losses</span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">{winRate}%</span>
                  <span className="stat-label">Win Rate</span>
                </div>
                <div className="stat-card cuts">
                  <span className="stat-value">{profile.totalCuts}</span>
                  <span className="stat-label">Cuts Taken</span>
                </div>
              </div>

              <div className="profile-id-section">
                <span className="profile-id-label">Player ID</span>
                <span className="profile-id-value">{playerId.slice(0, 8)}...</span>
              </div>
            </>
          ) : null}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Profile;
